import * as React from "react";
import { Dropdown, IDropdownOption } from "@fluentui/react/lib/Dropdown";
import { ConstrainMode, IColumn, SelectionMode } from "@fluentui/react/lib/DetailsList";
import { connect } from "react-redux";
import _ from "lodash";
import { IUserProfile } from "../../../Auth/types";
import { showDialog, showToastMessage } from "../../../Dialog/actions";
import { RootState } from "../../../../store/store";
import Helper from "../../../../core/libraries/Helper";
import RbLabel, { LabelSize } from "../../../../bosch-react/components/label/RbLabel";
import RbButton, { ButtonSize } from "../../../../bosch-react/components/button/RbButton";
import DetailsListWrapper from "../../../../core/components/DetailsListWrapper/DetailsListWrapper";
import { Icon, IconButton, IListProps, ShimmeredDetailsList, TooltipHost } from "@fluentui/react";
import Configuration from "../../../../core/libraries/Configuration";
import { IValidation } from "./types";
import Color from "../../../../core/libraries/Color";
import { IDivision } from "../Division/types";
import { getDivisionsList } from "../../../../services/commonServices";
import { validateDepartments } from "../../services";
import { exportToExcel } from "../../../../services/excelServices";
import Constants from "../../../../core/libraries/Constants";
import { tooltipHostStyles } from "../types";

interface ValidationProps {
    userProfile: IUserProfile | undefined;
    showDialog: typeof showDialog;
    showToastMessage: typeof showToastMessage;
}

interface IState {
    selectedDivision: string | null,
    divisions: IDropdownOption[] | null,
    items: IValidation[] | null
}

class Validation extends React.Component<ValidationProps, IState> {

    constructor(props: ValidationProps) {
        super(props);

        this.state = {
            selectedDivision: null,
            divisions: [],
            items: null,
        };

        this.renderDivision = this.renderDivision.bind(this);
    }

    async componentDidMount() {
        const prevState = Helper.getSessionStorageWithExpiry(Constants.SESSION_STORAGE_KEYS.validationComponentState);
        if (!!prevState)
            this.setState({ ...prevState });
        else {
            let rs: IDropdownOption[] = [];
            const response = await getDivisionsList();
            const divisions: IDivision[] = !!(response.data) && response.data;
            divisions.forEach((division) => {
                rs.push({
                    key: division.title || "",
                    text: division.longTitle || "",
                });
            });
            rs.unshift({ key: "All", text: "--- All ---" });
            this.setState({ divisions: rs });
        }
    }

    columns: IColumn[] = [
        {
            key: "departmentName",
            name: "Department Name",
            fieldName: "DepartmentName",
            currentWidth: Helper.resizeColumnByScreenWidth(20),
            minWidth: Helper.resizeColumnByScreenWidth(20),
            isResizable: true,
            onRender: (item: IValidation) => {
                return (
                    <RbLabel size={LabelSize.Small}>{item.departmentName}</RbLabel>
                );
            },
        },
        {
            key: "hod",
            name: "Head of Department",
            fieldName: "HoD",
            currentWidth: Helper.resizeColumnByScreenWidth(20),
            minWidth: Helper.resizeColumnByScreenWidth(20),
            isResizable: true,
            onRender: (item: IValidation) => {
                return <RbLabel size={LabelSize.Small}>{item.departmentHead}</RbLabel>;
            },
        },
        {
            key: "rnd",
            name: "RnD",
            fieldName: "RnD",
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            onRender: (item: IValidation) => {
                return <RbLabel size={LabelSize.Small}>{item.countRnD}</RbLabel>;
            }
        },
        {
            key: "ll",
            name: "LL",
            fieldName: "LL",
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true, onRender: (item: IValidation) => {
                return <RbLabel size={LabelSize.Small}>{item.countLL}</RbLabel>;
            }
        },
        {
            key: "thesis",
            name: "Thesis",
            fieldName: "Thesis",
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            onRender: (item: IValidation) => {
                return <RbLabel size={LabelSize.Small}>{item.countThesis}</RbLabel>;
            }
        },
        {
            key: "paper",
            name: "Paper",
            fieldName: "Paper",
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            onRender: (item: IValidation) => {
                return <RbLabel size={LabelSize.Small}>{item.countPaper}</RbLabel>;
            }
        },
        {
            key: "status",
            name: "Validation Status",
            fieldName: "Status",
            currentWidth: Helper.resizeColumnByScreenWidth(20),
            minWidth: Helper.resizeColumnByScreenWidth(20),
            isResizable: true,
            onRender: (item: IValidation) => {
                return !!item.departmentHead ? (
                    <Icon
                        iconName="Accept"
                        style={{ color: Color.GREEN, fontWeight: "bold" }}
                    />
                ) : (
                    <Icon
                        iconName="Clear"
                        style={{ color: Color.RED, fontWeight: "bold" }}
                    />
                );
            }
        },
    ]

    shimmeredDetailsListProps: IListProps = {
        renderedWindowsAhead: 0,
        renderedWindowsBehind: 0,
    };

    render() {
        const { items } = this.state
        let element = (
            <div className="ms-Grid">
                <div className="ms-Grid-row common-padding-row">
                    {this.renderDivision()}
                    {/* Button "Export to excel" */}
                    <RbButton
                        disabled={!items || items.length === 0}
                        label="Export to Excel"
                        size={ButtonSize.Small}
                        onClick={
                            async () => {
                                !!items && items.length > 0 && await exportToExcel(items, "Validation_Data")
                            }
                        }
                    />
                    <TooltipHost
                        content={"Helps users identify which departments within the selected division are valid or invalid. 'Invalid' indicates that the department either lacks a head, is obsolete, or has been removed from Bosch's organization."}
                        styles={tooltipHostStyles}
                        calloutProps={{ gapSpace: 10 }}
                    >
                        <IconButton
                            iconProps={{ iconName: "Info" }}
                            ariaLabel="Tab information"
                            title="Tab information"
                        />
                    </TooltipHost>
                </div>

                <DetailsListWrapper>
                    <ShimmeredDetailsList
                        setKey="set"
                        compact={true}
                        items={items || []}
                        constrainMode={ConstrainMode.unconstrained}
                        columns={this.columns}
                        enableShimmer={!items}
                        selectionMode={SelectionMode.none}
                        ariaLabelForShimmer="Content is being fetched"
                        ariaLabelForGrid="Pending order details"
                        listProps={this.shimmeredDetailsListProps}
                        onRenderDetailsHeader={Configuration.onRenderStickyDetailsHeader}
                        onShouldVirtualize={() => { return true }}
                    />
                </DetailsListWrapper>
            </div>
        );
        return element;
    }

    renderDivision() {
        return this.renderRetrieveTemplate(
            "Division",
            <Dropdown
                selectedKey={this.state.selectedDivision}
                placeholder="Choose a division ..."
                options={this.state.divisions || []}
                onChange={async (event, option) => {
                    !!option && !!option.key && this.setState({ selectedDivision: option.key.toString() }, async () => {
                        await this.handleValidatingDepartment(this.state.selectedDivision!)
                    })
                }}
            />
        );
    }

    handleValidatingDepartment = async (div: string) => {
        this.props.showDialog("Validating departments...")
        const items = await validateDepartments(div) || []
        items.length > 0 && this.setState({
            items: items
        }, () => {
            Helper.setSessionStorageWithExpiry(Constants.SESSION_STORAGE_KEYS.validationComponentState, this.state, 10)
        })
        this.props.showDialog(false)
    }

    renderRetrieveTemplate(label: string, field: any) {
        return (
            <div className="ms-Grid-col ms-sm5">
                <div className="ms-Grid">
                    <div className="ms-Grid-row common-padding-row">
                        <div className="ms-Grid-col ms-sm4">
                            <RbLabel hasPadding={true}>{label}</RbLabel>
                        </div>
                        <div className="ms-Grid-col ms-sm8">{field}</div>
                    </div>
                </div>
            </div>
        );
    }
}
const mapStateToProps = (state: RootState) => ({
    userProfile: state.auth.userProfile,
});
export default connect(mapStateToProps, { showDialog, showToastMessage })(
    Validation
);
